import { createFileRoute, useRouter } from '@tanstack/react-router';
import { RoleForm, type RoleFormDto } from '@/components/organisms/settings-roles/RoleForm';
import { usePermissionCatalog } from '@/hooks/settings/usePermissions';
import { useRoleDetail } from '@/hooks/settings/useRoles';
import { useUpdateRole } from '@/hooks/settings/useUpdateRole';
import { useHasPermission } from '@/hooks/usePermissions';
import { usePurchasingRoleVisibility } from '@/features/purchasing/visibility';

export const Route = createFileRoute('/_dashboard/dashboard/settings/permissions/$id/edit')({
  component: EditRoleSettingsRoute,
});

function EditRoleSettingsRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const decodedId = decodeURIComponent(id);
  const canEdit = useHasPermission('roles:edit');
  const { data: fullCatalog } = usePermissionCatalog();
  // Purchasing and inventory groups only where their flag is on; the role
  // still saves every permission it holds (PLAT-FF-1).
  const visibility = usePurchasingRoleVisibility();
  const catalog = fullCatalog && {
    ...fullCatalog,
    resources: fullCatalog.resources.filter((entry) => visibility.showResource(entry.resource)),
  };
  const { data: role, isLoading: roleLoading } = useRoleDetail(decodedId);
  const updateRole = useUpdateRole();

  function goToDetail() {
    void router.navigate({
      to: '/dashboard/settings/permissions/$id',
      params: { id: decodedId },
    });
  }

  function handleSubmit(dto: RoleFormDto) {
    if (!role) return;
    updateRole.mutate({ roleId: role.id, ...dto }, { onSuccess: goToDetail });
  }

  // `roles:edit` is the gate for entering edit; if the permission is missing,
  // fall back to the read-only detail rather than rendering the form.
  if (!canEdit) {
    goToDetail();
    return null;
  }

  return (
    <RoleForm
      mode="edit"
      role={role ?? null}
      catalog={catalog}
      isLoadingRole={roleLoading && !role}
      isPending={updateRole.isPending}
      onBack={goToDetail}
      onSubmit={handleSubmit}
    />
  );
}
